import { Button, InputLabel, Typography, useTheme } from '@mui/material';
import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import './style.css';
import {
  ImageItem,
  handlePersistedFiles,
  persistFiles,
  removePersistedFiles,
} from '../../../utils/form/appointmentForm';

interface ImageInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  images?: ImageItem[];
  setImages: React.Dispatch<React.SetStateAction<ImageItem[]>>;
  persistImages?: boolean;
}

function ImageInput({
  name,
  label,
  placeholder,
  images,
  setImages,
  persistImages,
}: ImageInputProps) {
  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    // only load persisted files if no files are already present
    if (images?.length || !persistImages) return;
    const getPersistedFiles = async () => {
      const newImgs = await handlePersistedFiles();
      if (newImgs.length) setImages(newImgs);
    };
    getPersistedFiles();
  }, [images, setImages, persistImages]);

  const onInputChange = React.useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      const newFiles = Array.from(files);
      const newImgs = newFiles.map(file => ({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
      }));

      // persist files to idb/session storage
      setImages(c => [...(c ?? []), ...newImgs]);
      if (persistImages) persistFiles(newImgs, newFiles);

      // allow re-uploading the same file
      inputRef.current!.value = '';
    },
    [setImages, persistImages],
  );

  const onRemoveImage = React.useCallback(
    async (imageId: string) => {
      const newImgs = images?.filter(img => img.id !== imageId);
      setImages(newImgs ?? []);
      if (persistImages) removePersistedFiles([imageId]);
    },
    [images, setImages, persistImages],
  );

  return (
    <div className="input image-upload-input">
      {!!label && (
        <InputLabel htmlFor={name}>
          <Typography variant="h5">{label}</Typography>
        </InputLabel>
      )}

      <div className="image-input-container">
        <Button
          variant="outlined"
          type="button"
          className="trigger-button"
          onClick={() => inputRef.current?.click()}>
          <AddIcon color="primary" fontSize="small" />
          <Typography color="primary" align="center" fontWeight="bold">
            Add Photos
          </Typography>
        </Button>

        <div className="images-list">
          {images?.map(image => {
            if (!image?.url) return null;
            return (
              <div key={image.id} className="image-upload-preview">
                <img src={image.url} alt="img" />
                <button
                  className="remove-image"
                  onClick={() => onRemoveImage(image.id)}
                  type="button">
                  <DeleteIcon
                    fontSize="small"
                    htmlColor={theme.palette.darkGrey.main}
                  />
                </button>
              </div>
            );
          })}
        </div>

        <input
          id={name}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          name={name}
          placeholder={placeholder}
          ref={inputRef}
          onChange={e => onInputChange(e.target.files)}
        />
      </div>
    </div>
  );
}

export default ImageInput;
